import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CircleCheck, Info, X } from "lucide-react";
import { Button } from './ui/button';
import { safeExternalHref } from '@/lib/safeUrl';
import { useTranslation } from '@/hooks/useTranslation';

interface ServiceNotification {
  id: string;
  textContent: string;
  backgroundColor: 'lavender' | 'periwinkle' | 'sky' | 'pewter' | 'mint' | 'sage' | 'peach' | 'gold' | 'coral' | 'rose';
  notificationType: 'info' | 'warning' | 'success' | 'urgent';
  isDismissible: boolean;
  ctaText: string | null;
  ctaUrl: string | null;
  activeStart: string;
  activeEnd: string;
  isActive: boolean;
}

// Map notification types to icons
const typeIcons = {
  info: Info,
  warning: AlertTriangle,
  success: CircleCheck,
  urgent: AlertCircle,
};

/* The ten swatches an admin picks the banner's colour from are TOKENS now
   (index.css §THE SERVICE BANNER): a light and a dark value per swatch, and a
   foreground — near-black on the light ones, white on the dark — that changes
   with them. They were hex literals here, behind a file-wide lint disable,
   with a fixed grey-or-white text class hand-paired at every one. The tone is a
   data attribute; the stylesheet does the rest. */
const TONES = new Set<ServiceNotification['backgroundColor']>([
  'lavender', 'periwinkle', 'sky', 'pewter', 'mint', 'sage', 'peach', 'gold', 'coral', 'rose',
]);

// Simple markdown link parser: [text](url) -> <a>text</a>
function parseMarkdownLinks(text: string | null | undefined): React.ReactNode {
  if (!text) return '';
  
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // Add the link. The URL comes from admin-authored markdown and this banner
    // renders on every page for every user — including the unauthenticated login
    // screen, via the public /api/service-notifications/active route. Reject any
    // non-http(s) scheme so a `javascript:` target can't execute on our origin.
    const href = safeExternalHref(match[2]);
    parts.push(
      href ? (
        <a
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline font-semibold"
          data-testid={`link-notification-${match.index}`}
        >
          {match[1]}
        </a>
      ) : (
        <span key={match.index} className="font-semibold">{match[1]}</span>
      )
    );

    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

export function ServiceNotificationBanner() {
  const t = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);

  const { data: notification } = useQuery<ServiceNotification | null>({
    queryKey: ['/api/service-notifications/active'],
    // Service banners change rarely — 10-min poll + refetch on focus
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Check if this notification was previously dismissed
  useEffect(() => {
    if (notification?.id) {
      const dismissedId = sessionStorage.getItem('dismissed-notification-id');
      setIsDismissed(dismissedId === notification.id);
    } else {
      setIsDismissed(false);
    }
  }, [notification?.id]);

  const handleDismiss = () => {
    if (notification?.id) {
      sessionStorage.setItem('dismissed-notification-id', notification.id);
      setIsDismissed(true);
    }
  };

  // Don't render if no notification or if dismissed
  // `?.id`, not truthiness: the endpoint answers null when nothing is active,
  // and a fixture that answered [] once rendered an empty 24px banner.
  if (!notification?.id || isDismissed) {
    return null;
  }

  const Icon = typeIcons[notification.notificationType];
  // Same scheme allow-list as the inline markdown links above.
  const ctaHref = safeExternalHref(notification.ctaUrl);

  return (
    <div
      /* Rendered INSIDE the top bar, under its content row (AppShell's header;
         after AuthPageShell's), never above it: the top of the screen is then
         always the bar's own ground — the colour the root top-edge strip is
         drawn in — and the top band (the iOS strip, an iPadOS window's
         controls) is padded once, by the bar. It used to sit in flow above
         the header, padding the top band itself: the root strip washed its
         top 12px, and a windowed iPad got the band twice. */
      className="service-banner relative z-banner border-b"
      data-tone={TONES.has(notification.backgroundColor) ? notification.backgroundColor : 'lavender'}
      role="alert"
      aria-live="polite"
      data-testid="service-notification-banner"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="relative flex items-center justify-between gap-3 px-3 py-3 sm:px-4 sm:py-3.5">
          {/* Desktop spacer for centering - hidden on mobile */}
          <div className="hidden sm:block w-8 shrink-0" aria-hidden="true" />

          {/* Main content - centered on desktop, left-aligned on mobile */}
          <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0 justify-center">
            {Icon && (
              <Icon 
                className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" 
                data-testid="icon-notification-type" 
              />
            )}
            
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 sm:gap-x-3 sm:gap-y-2 justify-center min-w-0">
              <p className="text-caption leading-snug sm:text-body sm:leading-[1.55] font-medium text-center" data-testid="text-notification-message">
                {parseMarkdownLinks(notification.textContent)}
              </p>

              {notification.ctaText && ctaHref && (
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="service-banner-action shrink-0 text-caption sm:text-body h-7 sm:h-8"
                  data-testid="button-notification-cta"
                >
                  <a href={ctaHref} target="_blank" rel="noopener noreferrer">
                    {notification.ctaText}
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Close button - always visible when dismissible */}
          {notification.isDismissible ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="service-banner-action h-7 w-7 sm:h-8 sm:w-8 shrink-0 border-transparent bg-transparent"
              aria-label={t("shellNudgeDismissNotification", "Dismiss Notification")}
              data-testid="button-dismiss-notification"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          ) : (
            <div className="hidden sm:block w-8 shrink-0" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
}
